
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';

export function Landing() {
    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Hero Section */}
            <section
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(180deg, var(--color-bg-primary) 0%, var(--color-bg-secondary) 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Background Effect */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(circle at 20% 50%, rgba(138, 31, 42, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(30, 42, 64, 0.1) 0%, transparent 50%)`,
                    }}
                />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    style={{
                        textAlign: 'center',
                        maxWidth: '900px',
                        padding: 'var(--spacing-2xl)',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <motion.h1
                        style={{
                            fontSize: '4rem',
                            fontWeight: 700,
                            marginBottom: 'var(--spacing-lg)',
                            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Plotline
                    </motion.h1>
                    <motion.p
                        style={{
                            fontSize: '1.5rem',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--spacing-2xl)',
                            lineHeight: 1.6,
                        }}
                    >
                        A modern, cinematic screenplay-writing platform.
                        <br />
                        Real-time collaboration. Cloud sync. Beautiful design.
                    </motion.p>
                    <div className="flex gap-md" style={{ justifyContent: 'center' }}>
                        <Link to="/signup">
                            <Button variant="primary" style={{ fontSize: '1.125rem', padding: 'var(--spacing-md) var(--spacing-xl)' }}>
                                Get Started
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="secondary" style={{ fontSize: '1.125rem', padding: 'var(--spacing-md) var(--spacing-xl)' }}>
                                Sign In
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* Features Section */}
            <section style={{ padding: 'var(--spacing-2xl)', backgroundColor: 'var(--color-bg-secondary)' }}>
                <div className="container">
                    <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)', fontSize: '2.5rem' }}>
                        Features
                    </h2>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: 'var(--spacing-xl)',
                        }}
                    >
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="card"
                                style={{ cursor: 'default' }}
                            >
                                <div style={{ fontSize: '2.5rem', marginBottom: 'var(--spacing-md)' }}>{feature.icon}</div>
                                <h3 style={{ marginBottom: 'var(--spacing-sm)' }}>{feature.title}</h3>
                                <p style={{ marginBottom: 0 }}>{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer
                style={{
                    padding: 'var(--spacing-xl)',
                    textAlign: 'center',
                    backgroundColor: 'var(--color-bg-primary)',
                    borderTop: '1px solid var(--color-border)',
                    color: 'var(--color-text-tertiary)',
                }}
            >
                <p>&copy; 2024 Plotline. All rights reserved.</p>
            </footer>
        </div>
    );
}

const features = [
    {
        icon: '🎬',
        title: 'Screenplay Editor',
        description: 'Professional editor with Fountain format support, syntax highlighting, and auto-formatting.',
    },
    {
        icon: '🤝',
        title: 'Real-Time Collaboration',
        description: 'Work together seamlessly with live cursors, presence indicators, and conflict-free editing.',
    },
    {
        icon: '☁️',
        title: 'Cloud Sync',
        description: 'Integrate with Nextcloud, Google Drive, and OneDrive for automatic synchronization.',
    },
    {
        icon: '🔐',
        title: 'Enterprise Security',
        description: 'OIDC authentication with multiple providers and role-based access control.',
    },
    {
        icon: '🏢',
        title: 'Organizations',
        description: 'Create team workspaces with member management and script sharing.',
    },
    {
        icon: '🎨',
        title: 'Beautiful Design',
        description: 'Cinematic dark theme optimized for distraction-free creative writing.',
    },
];
